import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The `max` most recent items, newest first. Items added within `intervalMs`
 * of each other land in one render.
 */
export function useBatchedRecentItems<T>(
  max: number,
  intervalMs: number,
): { items: T[]; add: (item: T) => void } {
  const [items, setItems] = useState<T[]>([]);
  const pendingRef = useRef<T[]>([]);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  const add = useCallback(
    (item: T) => {
      pendingRef.current = [item, ...pendingRef.current].slice(0, max);
      if (timeoutRef.current !== undefined) {
        return;
      }
      timeoutRef.current = window.setTimeout(() => {
        timeoutRef.current = undefined;
        const pending = pendingRef.current;
        pendingRef.current = [];
        setItems((current) => [...pending, ...current].slice(0, max));
      }, intervalMs);
    },
    [max, intervalMs],
  );

  return { items, add };
}
